<!--
  ConversationSummary — Phase 8.1 / M8.1.7.

  Banner above the thread that shows a 1–2 paragraph AI summary of the
  conversation, so the operator can act on a freshly claimed thread
  without reading every message.

  Visibility contract:
    - Renders only when the current operator is the assignee. The
      `enabled` flag on the underlying TanStack query enforces that the
      POST never fires for non-assignees (cost discipline — admins
      browsing someone else's thread don't burn LLM tokens).
    - Dismiss button hides for the remainder of THIS mount. Switching
      to another conversation and back re-mounts the component and
      restores the banner — by design, since the cached server-side
      summary is cheap to re-fetch and the operator may want it again.
    - Cache invalidation is centralised in App.svelte (M8.1.8) on
      `conversation.assignee.updated` (claim flipped to me) and
      `message.created` direction=INBOUND for the active thread.
      No realtime wiring here — this component just renders the query.

  Errors are surfaced as a small inline strip, not a thread-blocking
  modal. The operator can keep reading the thread; the banner stays out
  of the way after one dismiss.
-->
<script lang="ts">
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { aiSummary } from '../lib/api/queries.js';

  type Props = {
    conversationId: string;
    /** assignee.userId from the conversation row, or null when unassigned. */
    assigneeUserId: string | null;
    /** Current operator's userId from the session store. */
    currentUserId: string | null;
  };
  let { conversationId, assigneeUserId, currentUserId }: Props = $props();

  const queryClient = useQueryClient();

  // Only fetch when the operator is the assignee. If the claim flips
  // away mid-view, `enabled` flips to false and TanStack drops back to
  // the cached result (or "idle" if nothing's cached) — never refetches.
  const enabled = $derived(
    currentUserId !== null &&
      assigneeUserId !== null &&
      currentUserId === assigneeUserId,
  );

  const query = $derived(createQuery(aiSummary(conversationId, { enabled })));

  // Dismiss-for-this-conversation. Resets whenever conversationId
  // changes — ThreadPage in the router doesn't key on conversationId,
  // so this component instance is reused across thread switches and
  // we have to reset state ourselves rather than relying on remount.
  let dismissed = $state(false);
  $effect(() => {
    conversationId; // read for tracking
    dismissed = false;
  });

  function dismiss(): void {
    dismissed = true;
  }

  async function regenerate(): Promise<void> {
    // Invalidate, then explicitly refetch. Invalidate alone wouldn't
    // refetch when `enabled` is true and the previous result is fresh
    // by staleTime; we want a forced re-pull so the operator gets a
    // visibly new generation even if the cache marker would say "fresh".
    await queryClient.invalidateQueries({
      queryKey: ['conversations', conversationId, 'ai-summary'],
    });
    await $query.refetch();
  }

  // Human-readable "x min ago". No Intl.RelativeTimeFormat dependency
  // here — that's deferred until a later i18n pass.
  function formatAgo(iso: string): string {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '';
    const diffSec = Math.max(0, Math.round((Date.now() - then) / 1000));
    if (diffSec < 45) return 'just now';
    if (diffSec < 90) return '1 min ago';
    const min = Math.round(diffSec / 60);
    if (min < 60) return `${min} min ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr} h ago`;
    const d = Math.round(hr / 24);
    return `${d} d ago`;
  }

  // Short, operator-friendly error message. Auth failures get a
  // specific note since they usually mean "you lost the claim" — a
  // confusing-to-debug state otherwise.
  function formatError(err: unknown): string {
    const msg = err instanceof Error ? err.message : String(err);
    if (msg.includes(' 403 ')) return 'No access to summary for this conversation.';
    if (msg.includes(' 409 ')) return 'Summary not available right now.';
    return 'Could not load summary.';
  }

  const summary = $derived($query.data ?? null);
  const isPending = $derived($query.isPending && enabled);
  const isFetching = $derived($query.isFetching);
  const isError = $derived($query.isError);
</script>

{#if enabled && !dismissed}
  <aside class="summary" aria-label="AI summary of the conversation">
    {#if isPending}
      <div class="row">
        <div class="skeleton" aria-hidden="true">
          <span class="line w-1"></span>
          <span class="line w-2"></span>
          <span class="line w-3"></span>
        </div>
        <span class="sr-only">Generating summary…</span>
      </div>
    {:else if isError}
      <div class="row error">
        <span class="error-text">{formatError($query.error)}</span>
        <button type="button" class="link" onclick={regenerate}>Retry</button>
        <button type="button" class="link" onclick={dismiss}>Dismiss</button>
      </div>
    {:else if summary}
      <div class="row content">
        <p class="body">{summary.summary}</p>
        <div class="meta">
          <span class="tag" aria-label={summary.fromCache ? 'Cached summary' : 'Just generated'}>
            <span class="dot" aria-hidden="true">✨</span>
            <span>AI summary</span>
            <span class="sep" aria-hidden="true">·</span>
            <span class="ago">{formatAgo(summary.generatedAt)}</span>
          </span>
          <div class="actions">
            <button
              type="button"
              class="link"
              onclick={regenerate}
              disabled={isFetching}
              aria-label={isFetching ? 'Regenerating summary' : 'Regenerate summary'}
            >
              {isFetching ? 'Regenerating…' : 'Regenerate'}
            </button>
            <button type="button" class="link" onclick={dismiss} aria-label="Dismiss summary">
              Dismiss
            </button>
          </div>
        </div>
      </div>
    {/if}
  </aside>
{/if}

<style>
  .summary {
    padding: 10px 14px 12px;
    background: color-mix(in oklch, var(--widget-accent, oklch(75% 0.165 75)) 6%, var(--widget-bg, oklch(99% 0.003 250)));
    border-bottom: 1px solid var(--widget-border, oklch(92% 0.005 250));
  }

  .row {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .body {
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: var(--widget-fg, oklch(20% 0.01 250));
    white-space: pre-wrap;
  }

  .meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  .tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 600;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    letter-spacing: 0.02em;
  }
  .tag .dot {
    font-size: 12px;
  }
  .tag .sep {
    opacity: 0.5;
  }
  .tag .ago {
    font-weight: 500;
  }

  .actions {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .link {
    background: none;
    border: none;
    padding: 0;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    font: inherit;
    font-size: 11.5px;
    font-weight: 600;
    cursor: pointer;
  }
  .link:hover:not(:disabled) {
    color: var(--widget-fg, oklch(20% 0.01 250));
    text-decoration: underline;
  }
  .link:focus-visible {
    outline: 2px solid var(--widget-accent, oklch(75% 0.165 75));
    outline-offset: 2px;
    border-radius: 2px;
  }
  .link:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* --- Error state -------------------------------------------------------- */

  .error {
    flex-direction: row;
    align-items: center;
    gap: 10px;
  }
  .error-text {
    flex: 1;
    font-size: 12px;
    color: oklch(40% 0.16 25);
  }

  /* --- Loading skeleton --------------------------------------------------- */

  .skeleton {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 4px 0;
  }
  .line {
    height: 10px;
    border-radius: 4px;
    background: color-mix(
      in oklch,
      var(--widget-fg, oklch(20% 0.01 250)) 8%,
      transparent
    );
    animation: pulse 1.4s ease-in-out infinite;
  }
  .w-1 { width: 92%; }
  .w-2 { width: 86%; }
  .w-3 { width: 68%; }

  @keyframes pulse {
    0%, 100% { opacity: 0.55; }
    50%      { opacity: 0.9; }
  }

  /* Visually hidden but accessible to screen readers. */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
</style>
